import { useState, useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import { base44 } from "../api/base44Client";
import {
  Button,
  Box,
  Typography,
  Paper,
  CircularProgress,
  Container,
  Grid,
  AppBar,
  Toolbar,
} from "@mui/material";
import SummaryView from "../components/financing/SummaryView";
import { CheckCircle2, ArrowLeft, Download, Edit, Shield } from "lucide-react";
import { motion } from "framer-motion";

export default function Conferma() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      const params = new URLSearchParams(window.location.search);
      const recordId = params.get("record_id");
      if (recordId) {
        const records = await base44.entities.FinancingRequest.filter({
          id: recordId,
        });
        if (records.length > 0) setData(records[0]);
      }
      setLoading(false);
    }
    load();
  }, []);

  const handleDownloadJSON = () => {
    if (!data) return;
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `pratica-${data.pratica_id || "export"}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.default",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!data) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.default",
          p: 4,
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <Typography color="text.secondary">Pratica non trovata</Typography>
          <Button component={RouterLink} to="/" variant="text" sx={{ mt: 2 }}>
            Torna alla home
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <AppBar
        position="static"
        color="default"
        elevation={1}
        sx={{ bgcolor: "background.paper" }}
      >
        <Container maxWidth="md">
          <Toolbar disableGutters sx={{ py: 1, gap: 2 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 2,
                bgcolor: "primary.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Shield size={18} color="white" />
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              Richiesta Finanziamento
            </Typography>
          </Toolbar>
        </Container>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 8 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box sx={{ textAlign: "center", mb: 6 }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                bgcolor: "success.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 3,
              }}
            >
              <CheckCircle2 size={32} color="white" />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
              Richiesta inviata!
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ maxWidth: 480, mx: "auto", mb: 1 }}
            >
              La tua richiesta di finanziamento è stata inviata con successo.
              Riceverai una conferma via email.
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontFamily: "monospace" }}
            >
              Pratica:{" "}
              <Box component="span" sx={{ fontWeight: 700 }}>
                {data.pratica_id}
              </Box>
            </Typography>
          </Box>
        </motion.div>

        <Paper variant="outlined" sx={{ p: 3, borderRadius: 4, mb: 4 }}>
          <SummaryView data={data} />
        </Paper>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Button
              fullWidth
              variant="outlined"
              size="large"
              onClick={handleDownloadJSON}
              startIcon={<Download size={18} />}
              sx={{ textTransform: "none", borderRadius: 2.5, py: 1.5 }}
            >
              Scarica riepilogo
            </Button>
          </Grid>
          {data.stato_pratica !== "inviata" && (
            <Grid item xs={12} sm={4}>
              <Button
                fullWidth
                variant="outlined"
                size="large"
                component={RouterLink}
                to={`/?record_id=${data.id}`}
                startIcon={<Edit size={18} />}
                sx={{ textTransform: "none", borderRadius: 2.5, py: 1.5 }}
              >
                Modifica dati
              </Button>
            </Grid>
          )}
          <Grid item xs={12} sm={4}>
            <Button
              fullWidth
              variant="contained"
              size="large"
              component={RouterLink}
              to="/admin"
              startIcon={<ArrowLeft size={18} />}
              sx={{ textTransform: "none", borderRadius: 2.5, py: 1.5 }}
            >
              Torna alle pratiche
            </Button>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
